import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Calendar, Music, Edit2, PlusCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import ReviewComponent from '@/components/shared/review/ReviewComponent';
import { formatSchedule } from '@/utils/formatters';
import DisplayEditForm from './DisplayEditFormTabbed';
import DisplayView from './DisplayView';
import { Display as DbDisplay } from '@/types/sequence';
import { UnsavedChangesDialog } from '@/components/ui/unsaved-changes-dialog';

// The type expected by DisplayView and DisplayEditForm
interface ViewDisplay {
  id: string;
  name: string;
  description: string;
  image_url: string;
  photos: string[];
  location: string;
  latitude: number | null;
  longitude: number | null;
  schedule: string;
  display_type: string;
  holiday_type: string;
  review_rating: number;
  songCount: number;
  holidays: string[];
  city?: string;
  state?: string;
}

interface DisplayTabProps {
  userDisplay: DbDisplay | null;
  onDeleteDisplay: (displayId: string) => void;
  currentUserId: string;
  isOwner: boolean;
  onBeforeNavigate?: () => boolean; // Returns true if navigation is allowed
  onRequestNavigation?: (callback: () => void) => void; // Request to navigate, receives callback
}

const convertToViewDisplay = (dbDisplay: DbDisplay): ViewDisplay => ({
  id: dbDisplay.id.toString(),
  name: dbDisplay.name,
  description: dbDisplay.description || '',
  image_url: dbDisplay.image_url || '',
  photos: (dbDisplay as any).photos || [dbDisplay.image_url].filter(Boolean),
  location: dbDisplay.location,
  latitude: dbDisplay.latitude || null,
  longitude: dbDisplay.longitude || null,
  schedule: dbDisplay.schedule || {} as any,
  display_type: dbDisplay.display_type || 'Unknown',
  holiday_type: dbDisplay.holiday_type || 'Unknown',
  review_rating: dbDisplay.rating || 0,
  songCount: (dbDisplay as any).songCount || 0,
  holidays: dbDisplay.holidays || [],
  city: (dbDisplay as any).city,
  state: (dbDisplay as any).state
});

const convertToDbDisplay = (viewDisplay: ViewDisplay): Partial<DbDisplay> => ({
  id: parseInt(viewDisplay.id),
  name: viewDisplay.name,
  description: viewDisplay.description,
  image_url: viewDisplay.image_url,
  photos: viewDisplay.photos,
  location: viewDisplay.location,
  latitude: viewDisplay.latitude,
  longitude: viewDisplay.longitude,
  schedule: viewDisplay.schedule || null,  // Already an object, no JSON parsing needed
  display_type: viewDisplay.display_type,
  holiday_type: viewDisplay.holiday_type,
  holidays: viewDisplay.holidays,
  city: viewDisplay.city,
  state: viewDisplay.state
} as any);

const DisplayTab: React.FC<DisplayTabProps> = ({
  userDisplay,
  onDeleteDisplay,
  currentUserId,
  isOwner,
  onBeforeNavigate,
  onRequestNavigation
}) => {
  const [display, setDisplay] = useState<ViewDisplay | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Owner check is now passed as prop
  // const isOwner = userDisplay && userDisplay.user_id === currentUserId;

  useEffect(() => {
    // Only update if we're viewing a different display or not currently editing
    if (userDisplay && (!display || display.id !== userDisplay.id.toString()) && !isEditing) {
      setDisplay(convertToViewDisplay(userDisplay));
    } else if (!userDisplay) {
      setDisplay(null);
    }
  }, [userDisplay?.id, isEditing]);

  // Check navigation permission when parent requests
  useEffect(() => {
    if (onRequestNavigation) {
      onRequestNavigation((proceedCallback) => {
        if (isEditing && hasUnsavedChanges) {
          setPendingNavigation(() => proceedCallback);
          setShowUnsavedDialog(true);
        } else {
          proceedCallback();
        }
      });
    }
  }, [isEditing, hasUnsavedChanges, onRequestNavigation]);

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => setIsEditing(false));
      setShowUnsavedDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const handleSaveChanges = async (updatedDisplay: ViewDisplay) => {
    const dbDisplay = convertToDbDisplay(updatedDisplay);

    // Save to database
    const { error } = await supabase
      .from('displays')
      .update(dbDisplay)
      .eq('id', parseInt(updatedDisplay.id));

    if (error) {
      console.error('Error saving display:', error);
      toast({
        title: 'Error',
        description: 'Failed to save changes. Please try again.',
        variant: 'destructive',
      });
      return;
    }

    setDisplay(convertToViewDisplay({ ...userDisplay!, ...dbDisplay }));
    setHasUnsavedChanges(false);
    setIsEditing(false);
    toast({
      title: 'Success',
      description: 'Display updated successfully',
    });
  };

  const handleDialogSave = () => {
    if (display) {
      handleSaveChanges(display);
      setShowUnsavedDialog(false);
      if (pendingNavigation) {
        pendingNavigation();
        setPendingNavigation(null);
      }
    }
  };

  const handleDialogDiscard = () => {
    setHasUnsavedChanges(false);
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    } else {
      setIsEditing(false);
    }
  };

  const handleDialogCancel = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  if (!display) {
    if (isOwner) {
      return (
        <div className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4">Create Your First Display</h2>
          <p className="text-muted-foreground mb-6">
            Share your holiday light display with the community!
          </p>
          <Button onClick={() => navigate('/display/edit')}>
            <PlusCircle className="mr-2" size={16} />
            Create Display
          </Button>
        </div>
      );
    } else {
      return (
        <div className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4">No Display</h2>
          <p className="text-muted-foreground">
            This user hasn't created a display yet.
          </p>
        </div>
      );
    }
  }

  return (
    <div>
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onSave={handleDialogSave}
        onDiscard={handleDialogDiscard}
        onCancel={handleDialogCancel}
      />

      {isEditing && isOwner ? (
        <DisplayEditForm
          display={display}
          onSave={handleSaveChanges}
          onCancel={handleCancelEdit}
          onChange={(updatedDisplay) => {
            if (updatedDisplay) {
              setDisplay(updatedDisplay);
            }
          }}
          onUnsavedChangesChange={setHasUnsavedChanges}
        />
      ) : (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">{isOwner ? "My Display" : "Display"}</h2>
            {isOwner && (
              <div className="flex gap-2">
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  <Edit2 className="mr-2" size={16} />
                  Edit Display
                </Button>
                <Button onClick={() => onDeleteDisplay(userDisplay.id)} variant="destructive">
                  Delete Display
                </Button>
              </div>
            )}
          </div>
          <DisplayView display={display} />
        </>
      )}
    </div>
  );
};

export default DisplayTab;
